/**
 * The reviewer's docking-site choice for a multi-structure workspace.
 *
 * A campaign docks into one structure. When a workspace holds more than one,
 * WHICH one is a scientific decision, and the reviewer makes it —
 * `review-feedback-ledger.md` item 54: *"that should be a choice the user
 * decides."* This module turns the loaded structures into that choice and
 * decides what is offerable.
 *
 * Two things it deliberately does not do:
 *
 * - It does not introduce a second place where the role is stored. Picking a site
 *   writes the same target flag the Project / Targets checkbox writes, via the
 *   same `SetProteinTarget` action, so the two panels cannot disagree.
 * - It does not offer "dock into both and compare" as available. Docking into two
 *   sites is a real request and the option is shown, but nothing downstream of
 *   the dock step is attributed per structure: ranked leads would put two
 *   proteins' interaction energies in one column and let the larger molecule win.
 *   An option that is visible and disabled, with the reason attached, is a
 *   roadmap; an option that runs and prints a meaningless ranking is a defect.
 *
 * Real display names are fine here. The masking boundary governs what crosses to
 * the Orchestrator and the LLM; this panel belongs to the person who owns the
 * data. Only the masked `dockingSite` reaches the signed submission.
 *
 * This is not the only place a role gets set, and is not meant to be.
 * `ImportProteinPane/Tabs/SelectivityControl.jsx` already asks On target /
 * Off target while a structure is being ADDED, so a workspace built through that
 * pane arrives with roles decided. What it cannot cover is a restored session: a
 * saved `.bmaps` marks every structure as a target, so the choice has to be
 * offerable after the fact too.
 *
 * @module ui/dialog/WorkflowPane/dockingSiteChoice
 */

export const DOCK_BOTH = 'dock-both-and-compare';

export const DOCK_BOTH_UNAVAILABLE_REASON = 'Comparing two structures needs per-structure '
    + 'scoring, which this campaign does not produce yet — one ranking cannot hold energies '
    + 'computed against different proteins.';

/**
 * The docking-site choice for a set of loaded structures.
 *
 * @param {{key: string, name: string, isTarget: boolean}[]} structures loaded
 *   structures in workspace order. `key` is whatever the caller can act on later.
 * @returns {{
 *   applicable: boolean,
 *   value: ?string,
 *   options: {value: string, label: string, disabled: boolean, reason: ?string}[],
 *   needsChoice: boolean,
 *   helperText: ?string,
 * }}
 *   `applicable` is false for a single structure — there is no choice to present,
 *   and presenting one would imply the reviewer had a decision they did not.
 *   `needsChoice` means the workspace cannot run until they pick: either nothing
 *   is marked, or more than one thing is.
 */
export function dockingSiteChoice(structures = []) {
    const loaded = Array.isArray(structures) ? structures.filter(Boolean) : [];
    if (loaded.length < 2) {
        return {
            applicable: false,
            value: null,
            options: [],
            needsChoice: false,
            helperText: null,
        };
    }
    const targets = loaded.filter((structure) => structure.isTarget === true);
    const options = [
        ...loaded.map((structure) => ({
            value: structure.key,
            label: structure.name,
            disabled: false,
            reason: null,
        })),
        {
            value: DOCK_BOTH,
            label: 'Both, to compare selectivity',
            disabled: true,
            reason: DOCK_BOTH_UNAVAILABLE_REASON,
        },
    ];
    // Exactly one target is a resolved choice; zero or several is an open one. A
    // saved session restores every structure as a target (TargetInfo defaults to
    // `{isTarget: true}`), so "several" is the state a loaded multi-protein
    // workspace arrives in — the common case, not an edge case.
    const resolved = targets.length === 1 ? targets[0] : null;
    return {
        applicable: true,
        value: resolved ? resolved.key : '',
        options,
        needsChoice: !resolved,
        helperText: resolved
            ? `Docking into ${resolved.name}. The other `
                + `${loaded.length === 2 ? 'structure stays' : `${loaded.length - 1} structures stay`} `
                + 'loaded for reference, and their compounds are out of scope.'
            : `This workspace has ${loaded.length} structures and `
                + `${targets.length === 0
                    ? 'none is marked as the target'
                    : `${targets.length} are marked as targets`}. `
                + 'Choose which one to dock into.',
    };
}
